import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Pagination, Select, Spin } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoExperimentRecords } from '../../demo/demoBrowseMocks';
import {
  experimentRecordService,
  ExperimentDiscipline,
  ExperimentRecord,
  ExperimentStatus
} from '../../services/experimentRecords/experimentRecordService';
import '../../styles/literature-stitch.css';
import '../../styles/experiment-records-stitch.css';
import '../MemberManagement.css';

const { Option } = Select;

const DISCIPLINE_LABEL: Record<ExperimentDiscipline, string> = {
  chemistry: '化学',
  biology: '生物',
  materials: '材料',
  general: '综合'
};

function formatTime(iso: string | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/** 列表卡片右上角：相对更新时间（中文） */
function formatRelativeUpdated(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - d) / 1000));
    if (diffSec < 60) return `${diffSec} 秒前更新`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} 分钟前更新`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} 小时前更新`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 45) return `${diffDay} 天前更新`;
    return `更新于 ${formatTime(iso)}`;
  } catch {
    return formatTime(iso);
  }
}

function snippet(rec: ExperimentRecord): string {
  const s = (rec.summary || '').trim();
  if (s) return s.slice(0, 280);
  const strip = (rec.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return strip.slice(0, 280) || '（暂无摘要）';
}

function canEditRow(rec: ExperimentRecord, userId: string | undefined, role: string | null): boolean {
  if (!userId) return false;
  const cid = rec.createdBy && (rec.createdBy as { _id?: string })._id;
  if (cid && String(cid) === String(userId)) return true;
  if (role === '管理员' || role === '超级管理员') return true;
  return false;
}

function refLen(arr?: unknown[]): number {
  return Array.isArray(arr) ? arr.length : 0;
}

function userInitials(rec: ExperimentRecord): string {
  const u = (rec.createdBy || rec.updatedBy) as { name?: string; username?: string } | undefined;
  const name = (u?.name || u?.username || '').trim();
  if (!name) return '?';
  const parts = name.split(/[\s·.，,]+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[1][0] || '';
    return (a + b).toUpperCase().slice(0, 2);
  }
  return name.slice(0, 2).toUpperCase();
}

const ExperimentRecords: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoBrowseOnly, user, role } = useAuth();
  const userId = user?._id || user?.id;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ExperimentRecord[]>([]);
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [discipline, setDiscipline] = useState<ExperimentDiscipline | ''>('');
  const [status, setStatus] = useState<ExperimentStatus | ''>('');
  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoBrowseOnly) {
        let all = [...getDemoExperimentRecords()];
        if (discipline) all = all.filter((r) => r.discipline === discipline);
        if (status) all = all.filter((r) => r.status === status);
        if (q.trim()) {
          const qq = q.trim().toLowerCase();
          all = all.filter(
            (r) =>
              r.title.toLowerCase().includes(qq) ||
              (r.summary || '').toLowerCase().includes(qq) ||
              (r.tags || []).some((t) => t.toLowerCase().includes(qq))
          );
        }
        all.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const totalDemo = all.length;
        const start = (page - 1) * limit;
        setRows(all.slice(start, start + limit));
        setTotal(totalDemo);
        return;
      }
      const data = await experimentRecordService.getList({
        page,
        limit,
        q: q || undefined,
        discipline: discipline || undefined,
        status: status || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });
      setRows(data.experimentRecords);
      setTotal(data.pagination.total);
    } catch (e: unknown) {
      const err = e as Error;
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, discipline, status, isDemoBrowseOnly]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const commitSearch = () => {
    setQ(qDraft.trim());
    setPage(1);
  };

  const onDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm('确定删除该实验记录？此操作不可恢复。')) return;
    try {
      await experimentRecordService.delete(id);
      message.success('已删除');
      void fetchList();
    } catch (err: unknown) {
      message.error((err as Error).message || '删除失败');
    }
  };

  const goDetail = (id: string) => navigate(`/workbench/experiment-records/${id}`);
  const goEdit = (id: string) => navigate(`/workbench/experiment-records/${id}/edit`);

  const yearFromRecord = (rec: ExperimentRecord) => {
    try {
      return String(new Date(rec.updatedAt).getFullYear());
    } catch {
      return '—';
    }
  };

  const showingLabel = useMemo(() => {
    if (total === 0) return '暂无条目';
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, total);
    return `第 ${from}–${to} 条，共 ${total} 条`;
  }, [page, limit, total]);

  return (
    <div className="page-container full-height members-lims literature-lims-module er-page">
      <nav className="er-notebook-crumb" aria-label="breadcrumb">
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/')}>
          首页
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/workbench')}>
          工作台
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <span className="er-notebook-crumb-current">实验记录本</span>
      </nav>

      <header className="er-notebook-hero">
        <div>
          <h1 className="er-notebook-hero-title">实验记录本</h1>
          <p className="er-notebook-hero-sub">
            集中查看团队内的实验笔记与过程记录；支持按领域、状态筛选，化学模板与富文本正文可在详情中编辑。
          </p>
        </div>
        <div className="er-notebook-hero-actions">
          <button
            type="button"
            className="er-btn-filter-ghost"
            onClick={() => setFiltersExpanded((v) => !v)}
            aria-expanded={filtersExpanded}
          >
            <span className="material-symbols-outlined">filter_list</span>
            {filtersExpanded ? '收起筛选' : '筛选'}
          </button>
          <button
            type="button"
            className="mm-btn-primary"
            disabled={isDemoBrowseOnly}
            onClick={() => navigate('/workbench/experiment-records/new')}
          >
            <LimsNavIcon name="add" sizePx={18} />
            新建记录
          </button>
        </div>
      </header>

      {isDemoBrowseOnly && (
        <div className="er-alert er-alert--warn" role="status">
          当前为演示浏览模式，加入或创建团队后即可新建与编辑实验记录。
        </div>
      )}

      {filtersExpanded ? (
        <div className="er-notebook-filters">
          <div className="lit-toolbar-inner">
            <div className="lit-toolbar-search-row">
              <div className="mm-search lit-mm-search-grow">
                <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
                <input
                  type="search"
                  className="mm-search-input"
                  placeholder="搜索标题、正文、摘要、标签…"
                  value={qDraft}
                  onChange={(e) => setQDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitSearch();
                    }
                  }}
                  aria-label="搜索实验记录"
                />
              </div>
              <button type="button" className="mm-btn-primary lit-toolbar-submit" onClick={commitSearch}>
                <LimsNavIcon name="search" sizePx={18} />
                搜索
              </button>
            </div>
            <div className="lit-toolbar-filters">
              <Select
                className="lit-toolbar-select"
                allowClear
                placeholder="全部领域"
                value={discipline || undefined}
                onChange={(v) => {
                  setDiscipline((v as ExperimentDiscipline) || '');
                  setPage(1);
                }}
                style={{ minWidth: 140 }}
              >
                {(Object.keys(DISCIPLINE_LABEL) as ExperimentDiscipline[]).map((k) => (
                  <Option key={k} value={k}>
                    {DISCIPLINE_LABEL[k]}
                  </Option>
                ))}
              </Select>
              <Select
                className="lit-toolbar-select"
                allowClear
                placeholder="全部状态"
                value={status || undefined}
                onChange={(v) => {
                  setStatus((v as ExperimentStatus) || '');
                  setPage(1);
                }}
                style={{ width: 130 }}
              >
                <Option value="draft">草稿</Option>
                <Option value="final">已定稿</Option>
              </Select>
            </div>
          </div>
        </div>
      ) : null}

      <div className="er-notebook-shell">
        <div className="er-notebook-shell-head">
          <h2>实验记录列表</h2>
          <span className="er-notebook-shell-count">{loading ? '加载中…' : showingLabel}</span>
        </div>

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <div className="p-4">
              <div className="er-alert" style={{ margin: 0 }}>
                暂无记录。点击「新建记录」开始撰写；也可先展开「筛选」调整条件。未加入团队时请通过顶栏加入实验室。
              </div>
            </div>
          ) : (
            <div className="er-notebook-list">
              {rows.map((rec) => (
                <article key={rec._id} className="er-notebook-item">
                  <div className="er-notebook-item-inner">
                    <div className="er-notebook-main">
                      <div className="er-notebook-title-row">
                        <div className="er-notebook-title-with-avatar">
                          <div className="er-notebook-avatar er-notebook-avatar--inline" title={rec.createdBy?.name || rec.createdBy?.username || ''}>
                            {userInitials(rec)}
                          </div>
                          <button type="button" className="er-notebook-title" onClick={() => goDetail(rec._id)}>
                            {rec.title}
                          </button>
                        </div>
                        <span className="er-notebook-updated">{formatRelativeUpdated(rec.updatedAt)}</span>
                      </div>
                      <div className="er-notebook-tags">
                        <span className="er-notebook-tag--source">{DISCIPLINE_LABEL[rec.discipline]}</span>
                        <span className="er-notebook-tag--year">{yearFromRecord(rec)}</span>
                        <span className="er-notebook-tag--outline">{rec.status === 'draft' ? '草稿' : '已定稿'}</span>
                        {(rec.tags || []).slice(0, 4).map((t) => (
                          <span key={t} className="er-notebook-tag--outline">
                            {t}
                          </span>
                        ))}
                        {refLen(rec.relatedReservations) > 0 || refLen(rec.relatedLiteratures) > 0 ? (
                          <span className="er-notebook-tag--outline">
                            预约 {refLen(rec.relatedReservations)} · 文献 {refLen(rec.relatedLiteratures)}
                          </span>
                        ) : null}
                      </div>
                      <div className="er-notebook-snippet-box er-notebook-snippet-box--list">
                        <p>{snippet(rec)}</p>
                      </div>
                      <div className="er-notebook-actions-row">
                        <button type="button" className="er-notebook-action-open" onClick={() => goDetail(rec._id)}>
                          打开查看
                          <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                        {canEditRow(rec, userId, role) && !isDemoBrowseOnly ? (
                          <>
                            <button type="button" className="er-notebook-action-edit" onClick={() => goEdit(rec._id)}>
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                edit_note
                              </span>
                              编辑
                            </button>
                            <button
                              type="button"
                              className="er-notebook-action-delete er-btn-ghost er-btn-danger-ghost"
                              onClick={(e) => onDelete(e, rec._id)}
                            >
                              删除
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </Spin>

        {total > limit ? (
          <div className="er-notebook-pager">
            <Pagination
              current={page}
              pageSize={limit}
              total={total}
              showSizeChanger={false}
              onChange={(p) => setPage(p)}
            />
          </div>
        ) : null}
      </div>

      <div className="er-notebook-foot-cta">
        <span className="material-symbols-outlined" aria-hidden>
          edit_note
        </span>
        <h3>扩展你的研究记录？</h3>
        <p>在文献库中积累资料、在仪器模块完成预约后，可在单条实验记录中关联文献与本人预约，形成可追溯笔记链。</p>
        <div className="d-flex flex-wrap gap-3 justify-content-center">
          <Link className="er-notebook-foot-link" to="/workbench/experiment-records/shared">
            共享记录本
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              arrow_forward
            </span>
          </Link>
          <Link className="er-notebook-foot-link" to="/literature">
            前往文献库
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              arrow_forward
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ExperimentRecords;
